import { BarChart } from "@mui/x-charts/BarChart";
import { Typography, Box } from "@mui/material";
import TooltipForShowingUploadPerc from "./helpers/TooltipForShowingUploadPerc";

interface Props {
  data: any;
}
type ChartRow = {
  day: string;
  _dayRaw: string;
  Loaded: number;
  "Not Loaded": number;
  UploadPerc: number;
  __raw?: any;
};

const ConversionsUploadOverTimeGraph = ({ data }: Props) => {
  const graphData = data?.query_output || [];
  const aggregateByDay = (rows: ChartRow[]): ChartRow[] => {
    const map: Record<string, ChartRow> = {};

    for (const row of rows) {
      const key = row._dayRaw;

      if (!map[key]) {
        // clone row so we don't mutate original
        map[key] = { ...row };
      } else {
        map[key].Loaded += row.Loaded;
        map[key]["Not Loaded"] += row["Not Loaded"];
        map[key].UploadPerc += row.UploadPerc;
      }
    }

    return Object.values(map).sort(
      (a, b) => new Date(a._dayRaw).getTime() - new Date(b._dayRaw).getTime()
    );
  };
  const formatDateForAxis = (dateStr: string) => {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };
  const chartData = graphData.map((item: any) => {
    const loaded = Number(item.total_conversion_loaded ?? 0);
    const total = Number(item.total_conversion ?? 0);
    const notLoaded = Math.max(0, total - loaded);
    // const uploadPerc = Number(item.perc_conversions_loaded ?? 0) * 100;

    return {
      day: formatDateForAxis(item.day), // formatted for UI
      _dayRaw: item.day,
      Loaded: loaded,
      "Not Loaded": notLoaded,
      UploadPerc: total === 0 ? 0 : loaded / total,
      __raw: item,
    };
  });
  chartData.sort(
    (a: any, b: any) =>
      new Date(a._dayRaw).getTime() - new Date(b._dayRaw).getTime()
  );
  const aggregatedChartData = aggregateByDay(chartData);

  const barLabel = (item: any) => {
    const value = item?.value ?? null;
    if (value === null || value === 0) return null;
    return String(value);
  };

  return (
    <Box sx={{ width: "100%", height: 400 }}>
      <Typography variant="h5" sx={{ mb: 2, pl: 3.5, pt: 2 }}>
        Conversions Over Time
      </Typography>

      <BarChart
        dataset={aggregatedChartData}
        xAxis={[{ scaleType: "band", dataKey: "day", disableLine: true }]}
        yAxis={[
          {
            dataKey: "y",
            disableLine: true,
            disableTicks: true,
          },
        ]}
        margin={{ left: 45, right: 56, top: 25, bottom: 84 }}
        grid={{ vertical: false, horizontal: true }}
        series={[
          {
            dataKey: "Loaded",
            label: "Loaded",
            stack: "total",
            color: "#81c784",
          },
          {
            dataKey: "Not Loaded",
            label: "Unloaded",
            stack: "total",
            color: "#e57373",
          },
        ]}
        height={350}
        barLabel={barLabel}
        slotProps={{
          legend: {
            direction: "row",
            position: {
              vertical: "bottom",
              horizontal: "left",
            },
            labelStyle: {
              fontSize: 12,
            },
            itemMarkWidth: 12,
            itemMarkHeight: 2,
            markGap: 8,
            itemGap: 10,
            padding: 28,
          },
        }}
        sx={{
          [`& .MuiChartsAxis-tickLabel`]: {
            fill: "grey !important",
          },
          [`& .MuiChartsAxis-tick`]: {
            stroke: "grey !important",
          },
          [`& .MuiChartsLegend-root text`]: {
            fill: "grey !important",
          },
        }}
      >
        <TooltipForShowingUploadPerc dataset={aggregatedChartData} />
      </BarChart>
    </Box>
  );
};

export default ConversionsUploadOverTimeGraph;
